import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import BopBackLink from '../../../../components/BopBackLink';
import ApiInspector from '../../../../components/ApiInspector';
import PrintModal from '../../../../components/PrintModal';
import { apiFetch } from '../../../../lib/api';
import { showToast } from '../../../../components/Toast';
import { formatQuantity, parseItalianNumber } from '../../../../utils/formatters';
import { usePermesso } from '../../../../context/PermessiContext';
import { useBloccoRecordContesto } from '../../../../hooks/useBloccoRecordContesto';
import { useDirtyForm } from '../../../../hooks/useDirtyForm';
import { API_DOCS } from './SchedaDdt.api';
import './SchedaDdt.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type TipoDdt = 'VENDITA' | 'CANTIERE';
type StatoDdt = 'BOZZA' | 'EMESSO' | 'ANNULLATO';

interface RigaDdt {
  id: number;
  ordinePos: number;
  rigaOrdineId: number | null;
  componenteId: number | null;
  rigaDistintaId: number | null;
  articoloId: number | null;
  descrizione: string;
  umVendita: string | null;
  quantita: number | string;
  note: string | null;
  articolo: { id: number; codice: string; descrizioneBase: string } | null;
  rigaDistinta: { id: number; distintaId: number } | null;
}

interface LogEntry {
  id: number;
  utenteName: string;
  azione: string;
  dettaglio: string | null;
  createdAt: string;
}

interface DdtDettaglio {
  id: number;
  societaId: number;
  sedeId: number | null;
  commessaId: number | null;
  ordineId: number | null;
  tipo: TipoDdt;
  stato: StatoDdt;
  numero: string | null;
  dataEmissione: string | null;
  dataTrasporto: string | null;
  clienteRagioneSociale: string | null;
  clienteNome: string | null;
  clienteCognome: string | null;
  clientePartitaIva: string | null;
  clienteCodiceFiscale: string | null;
  clienteIndirizzo: string | null;
  clienteCap: string | null;
  clienteCitta: string | null;
  clienteProvincia: string | null;
  clienteEmail: string | null;
  clienteTelefono: string | null;
  destinazioneDescrizione: string | null;
  destinazioneIndirizzo: string | null;
  destinazioneCap: string | null;
  destinazioneCitta: string | null;
  destinazioneProvincia: string | null;
  causaleTrasporto: string | null;
  aspettoBeni: string | null;
  colli: number | null;
  pesoKg: number | string | null;
  porto: string | null;
  vettore: string | null;
  annotazioni: string | null;
  movimentoMagazzinoId: number | null;
  tipoDocumentoId?: number | null;
  tipoDocumento: {
    id: number;
    codice: string;
    nome: string;
    causaleMovimento: { codice: string; descrizione: string; tipo: string } | null;
  } | null;
  createdAt: string;
  ordine: { id: number; numero: string; flussoCommerciale: string } | null;
  commessa: {
    id: number;
    numero: string;
    descrizione: string | null;
    clienteRagioneSociale: string | null;
  } | null;
  movimentoMagazzino: { id: number; numero: number | string; anno: number; stato: string } | null;
  fatturaCollegata: { id: number; numero: string | null; stato: string } | null;
  createdBy: { id: number; nome: string; cognome?: string | null } | null;
  annullatoDa: { id: number; nome: string; cognome?: string | null } | null;
  righe: RigaDdt[];
  logs: LogEntry[];
}

// Riga in editing locale (quantità come stringa per l'input)
interface RigaLocale {
  key: string;
  rigaOrdineId: number | null;
  componenteId: number | null;
  rigaDistintaId: number | null;
  articoloId: number | null;
  codice: string | null;
  descrizione: string;
  umVendita: string | null;
  quantita: string;
  note: string;
}

// Opzione tipo documento per il select (bozza)
interface TipoDocumentoOption {
  id: number;
  codice: string;
  nome: string;
}

// Form testata "Destinazione e trasporto"
interface TrasportoForm {
  tipoDocumentoId: string;
  dataTrasporto: string;
  destinazioneDescrizione: string;
  destinazioneIndirizzo: string;
  destinazioneCap: string;
  destinazioneCitta: string;
  destinazioneProvincia: string;
  causaleTrasporto: string;
  aspettoBeni: string;
  colli: string;
  pesoKg: string;
  porto: string;
  vettore: string;
  annotazioni: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDateIT(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT');
}

function formatDateTimeIT(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('it-IT')} ${d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
}

function nomeUtente(u: { nome: string; cognome?: string | null } | null): string | null {
  if (!u) return null;
  return `${u.nome || ''} ${u.cognome || ''}`.trim() || null;
}

// Estrae il messaggio dagli errori API ({error: string | {code,message}})
function estraiErrore(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const payload = (err as { payload?: { error?: string | { code?: string; message?: string } } }).payload;
    const e = payload?.error;
    if (e && typeof e === 'object' && e.message) return e.message;
    if (typeof e === 'string' && e) return e;
    if (err instanceof Error && err.message && err.message !== '[object Object]') return err.message;
  }
  return fallback;
}

function toDateInput(s: string | null | undefined): string {
  if (!s) return '';
  return s.slice(0, 10);
}

let rigaKeyCounter = 0;
function nuovaKey(): string {
  rigaKeyCounter += 1;
  return `riga-${Date.now().toString(36)}-${rigaKeyCounter}`;
}

function rigaServerToLocale(r: RigaDdt): RigaLocale {
  return {
    key: r.id ? `srv-${r.id}` : nuovaKey(),
    rigaOrdineId: r.rigaOrdineId,
    componenteId: r.componenteId,
    rigaDistintaId: r.rigaDistintaId,
    articoloId: r.articoloId,
    codice: r.articolo?.codice ?? null,
    descrizione: r.descrizione,
    umVendita: r.umVendita,
    quantita: String(r.quantita ?? '').replace('.', ','),
    note: r.note || '',
  };
}

function trasportoFromServer(d: DdtDettaglio): TrasportoForm {
  return {
    tipoDocumentoId: d.tipoDocumento
      ? String(d.tipoDocumento.id)
      : (d.tipoDocumentoId ? String(d.tipoDocumentoId) : ''),
    dataTrasporto: toDateInput(d.dataTrasporto),
    destinazioneDescrizione: d.destinazioneDescrizione || '',
    destinazioneIndirizzo: d.destinazioneIndirizzo || '',
    destinazioneCap: d.destinazioneCap || '',
    destinazioneCitta: d.destinazioneCitta || '',
    destinazioneProvincia: d.destinazioneProvincia || '',
    causaleTrasporto: d.causaleTrasporto || '',
    aspettoBeni: d.aspettoBeni || '',
    colli: d.colli !== null && d.colli !== undefined ? String(d.colli) : '',
    pesoKg: d.pesoKg !== null && d.pesoKg !== undefined ? String(d.pesoKg).replace('.', ',') : '',
    porto: d.porto || '',
    vettore: d.vettore || '',
    annotazioni: d.annotazioni || '',
  };
}

// Campo label/valore in sola lettura
function Campo({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="ddt-scheda__campo">
      <span className="ddt-scheda__campo-label">{label}</span>
      <span className="ddt-scheda__campo-value">
        {value === null || value === undefined || value === '' ? (
          <span className="bop-text-muted">—</span>
        ) : (
          value
        )}
      </span>
    </div>
  );
}

// ── Pagina ────────────────────────────────────────────────────────────────────

export default function SchedaDdt() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = usePermesso();

  const [ddt, setDdt] = useState<DdtDettaglio | null>(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);

  // Editing testata (destinazione e trasporto)
  const [trasporto, setTrasporto] = useState<TrasportoForm | null>(null);
  const [trasportoSnapshot, setTrasportoSnapshot] = useState('');

  // Editing righe
  const [righe, setRighe] = useState<RigaLocale[]>([]);
  const [righeSnapshot, setRigheSnapshot] = useState('');

  // Opzioni tipo documento (select in bozza)
  const [tipiDocumento, setTipiDocumento] = useState<TipoDocumentoOption[]>([]);

  const [salvandoTrasporto, setSalvandoTrasporto] = useState(false);
  const [salvandoRighe, setSalvandoRighe] = useState(false);
  const [azioneInCorso, setAzioneInCorso] = useState(false);

  const etichetta = ddt ? (ddt.numero ?? 'bozza') : '';
  const isBozza = ddt?.stato === 'BOZZA';
  const isEmesso = ddt?.stato === 'EMESSO';
  const canEdit = can('commerciale.ddt', 'edit');
  const canDelete = can('commerciale.ddt', 'delete');
  const canFattureEdit = can('commerciale.fatture_attive', 'edit');
  const editabile = isBozza && canEdit;

  useBloccoRecordContesto({
    recordSocietaId: ddt?.societaId,
    label: ddt ? `DDT ${ddt.numero ?? 'bozza'}` : 'DDT',
  });

  const trasportoDirty =
    trasportoSnapshot !== '' && trasporto !== null && JSON.stringify(trasporto) !== trasportoSnapshot;
  const righeDirty = righeSnapshot !== '' && JSON.stringify(righe) !== righeSnapshot;
  useDirtyForm(editabile && (trasportoDirty || righeDirty), ddt ? `DDT ${etichetta}` : 'DDT');

  // Stampa / invio: motore di stampa, modulo DDT
  const [modalStampa, setModalStampa] = useState(false);
  const apriStampa = () => {
    if (editabile && (trasportoDirty || righeDirty)) {
      showToast('Salva le modifiche prima di stampare il DDT', 'error');
      return;
    }
    setModalStampa(true);
  };

  // ── Caricamento ─────────────────────────────────────────────────────────────

  const carica = useCallback(async () => {
    setLoading(true);
    setErrore(null);
    try {
      // @api GET /api/ddt/:id -> {"success":true,"data":{"id":"number","societaId":"number","tipo":"VENDITA|CANTIERE","stato":"BOZZA|EMESSO|ANNULLATO","numero":"string|null","dataEmissione":"string|null","dataTrasporto":"string|null","clienteRagioneSociale":"string|null","destinazioneDescrizione":"string|null","destinazioneIndirizzo":"string|null","destinazioneCap":"string|null","destinazioneCitta":"string|null","destinazioneProvincia":"string|null","causaleTrasporto":"string|null","aspettoBeni":"string|null","colli":"number|null","pesoKg":"number|null","porto":"string|null","vettore":"string|null","annotazioni":"string|null","movimentoMagazzinoId":"number|null","tipoDocumentoId":"number|null","tipoDocumento":{"id":"number","codice":"string","nome":"string","causaleMovimento":{"codice":"string","descrizione":"string","tipo":"string"}},"ordine":{"id":"number","numero":"string","flussoCommerciale":"string"},"commessa":{"id":"number","numero":"string","descrizione":"string","clienteRagioneSociale":"string"},"movimentoMagazzino":{"id":"number","numero":"number","anno":"number","stato":"string"},"fatturaCollegata":{"id":"number","numero":"string|null","stato":"string"},"createdBy":{"id":"number","nome":"string","cognome":"string"},"annullatoDa":{"id":"number","nome":"string","cognome":"string"},"righe":[{"id":"number","ordinePos":"number","rigaOrdineId":"number|null","componenteId":"number|null","rigaDistintaId":"number|null","articoloId":"number|null","descrizione":"string","umVendita":"string|null","quantita":"number","note":"string|null","articolo":{"id":"number","codice":"string","descrizioneBase":"string"},"rigaDistinta":{"id":"number","distintaId":"number"}}],"logs":[{"id":"number","utenteName":"string","azione":"string","dettaglio":"string","createdAt":"string"}]}}
      const r = await apiFetch<{ success: boolean; data?: DdtDettaglio; error?: string }>(`/api/ddt/${id}`);
      if (!r.success || !r.data) {
        setErrore(typeof r.error === 'string' ? r.error : 'DDT non trovato');
        setDdt(null);
        return;
      }
      const d = r.data;
      setDdt(d);
      const form = trasportoFromServer(d);
      setTrasporto(form);
      setTrasportoSnapshot(JSON.stringify(form));
      const righeLocali = (d.righe || []).map(rigaServerToLocale);
      setRighe(righeLocali);
      setRigheSnapshot(JSON.stringify(righeLocali));
    } catch (err: unknown) {
      setErrore(estraiErrore(err, 'Errore nel caricamento del DDT'));
      setDdt(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { carica(); }, [carica]);

  // Carica i tipi documento attivi della famiglia (solo in bozza)
  useEffect(() => {
    if (!ddt || ddt.stato !== 'BOZZA') { setTipiDocumento([]); return; }
    const famiglia = ddt.tipo === 'VENDITA' ? 'DDT_VENDITA' : 'DDT_CANTIERE';
    // @api GET /api/tipi-documento?societaId=&famiglia=&soloAttivi=true -> {"success":true,"data":[{"id":"number","codice":"string","nome":"string"}]}
    apiFetch<{ success: boolean; data: TipoDocumentoOption[] }>(
      `/api/tipi-documento?societaId=${ddt.societaId}&famiglia=${famiglia}&soloAttivi=true`
    )
      .then(r => setTipiDocumento(r.data || []))
      .catch(() => setTipiDocumento([]));
  }, [ddt]);

  // Opzioni select: tipi attivi + l'eventuale tipo già assegnato (anche se disattivato)
  const tipiDocumentoOptions = useMemo(() => {
    const td = ddt?.tipoDocumento;
    if (!td || tipiDocumento.some(t => t.id === td.id)) return tipiDocumento;
    return [...tipiDocumento, { id: td.id, codice: td.codice, nome: td.nome }];
  }, [tipiDocumento, ddt]);

  // ── Azioni header ───────────────────────────────────────────────────────────

  const emetti = async () => {
    if (!ddt) return;
    if (trasportoDirty || righeDirty) {
      showToast('Salva le modifiche prima di emettere il DDT', 'error');
      return;
    }
    if (!window.confirm('Emettere il DDT? Verrà assegnato il numero e generato il movimento di magazzino.')) return;
    setAzioneInCorso(true);
    try {
      // @api POST /api/ddt/:id/emetti -> {"success":true,"data":{"stato":"EMESSO","numero":"string","dataEmissione":"string","movimentoMagazzinoId":"number|null","magazzinoWarning":"string|null"}}
      const r = await apiFetch<{
        success: boolean;
        data?: { stato: string; numero: string; dataEmissione: string; movimentoMagazzinoId: number | null; magazzinoWarning: string | null };
      }>(`/api/ddt/${ddt.id}/emetti`, { method: 'POST' });
      showToast(`DDT emesso con numero ${r.data?.numero ?? ''}`.trim(), 'success');
      if (r.data?.magazzinoWarning) {
        showToast(r.data.magazzinoWarning, 'error');
      }
      await carica();
    } catch (err: unknown) {
      showToast(estraiErrore(err, "Errore nell'emissione del DDT"), 'error');
    } finally {
      setAzioneInCorso(false);
    }
  };

  const annulla = async () => {
    if (!ddt) return;
    const motivo = window.prompt(
      `Annullare il DDT ${ddt.numero ?? ''}? Il movimento di magazzino verrà stornato.\nMotivo (opzionale):`,
      ''
    );
    if (motivo === null) return; // annullato dall'utente
    setAzioneInCorso(true);
    try {
      // @api POST /api/ddt/:id/annulla {"motivo":"string?"} -> {"success":true,"data":{"stato":"ANNULLATO"}}
      await apiFetch<{ success: boolean; data?: { stato: string } }>(`/api/ddt/${ddt.id}/annulla`, {
        method: 'POST',
        body: JSON.stringify(motivo.trim() ? { motivo: motivo.trim() } : {}),
      });
      showToast(`DDT ${ddt.numero ?? ''} annullato`.trim(), 'success');
      await carica();
    } catch (err: unknown) {
      showToast(estraiErrore(err, "Errore nell'annullamento del DDT"), 'error');
    } finally {
      setAzioneInCorso(false);
    }
  };

  const elimina = async () => {
    if (!ddt) return;
    if (!window.confirm('Eliminare definitivamente la bozza del DDT?')) return;
    setAzioneInCorso(true);
    try {
      // @api DELETE /api/ddt/:id -> {"success":true}
      await apiFetch<{ success: boolean }>(`/api/ddt/${ddt.id}`, { method: 'DELETE' });
      showToast('DDT eliminato', 'success');
      navigate('/commerciale/attivo/ddt');
    } catch (err: unknown) {
      showToast(estraiErrore(err, "Errore nell'eliminazione del DDT"), 'error');
      setAzioneInCorso(false);
    }
  };

  const generaFattura = async () => {
    if (!ddt) return;
    if (!window.confirm('Generare la fattura immediata da questo DDT? Verrà creata una fattura in bozza.')) return;
    setAzioneInCorso(true);
    try {
      // @api POST /api/fatture/da-ddt {"ddtId":"number"} -> {"success":true,"data":{"id":"number"}}
      const r = await apiFetch<{ success: boolean; data?: { id: number } }>('/api/fatture/da-ddt', {
        method: 'POST',
        body: JSON.stringify({ ddtId: ddt.id }),
      });
      if (!r.success || !r.data) {
        showToast(estraiErrore({ payload: r }, 'Errore nella generazione della fattura'), 'error');
        return;
      }
      showToast('Fattura generata in bozza', 'success');
      navigate(`/commerciale/attivo/fatture/${r.data.id}`);
    } catch (err: unknown) {
      // 409 DDT_NON_FATTURABILE | GIA_FATTURATO: message dettagliato dal backend
      showToast(estraiErrore(err, 'Errore nella generazione della fattura'), 'error');
    } finally {
      setAzioneInCorso(false);
    }
  };

  // ── Salvataggio testata (destinazione e trasporto) ──────────────────────────

  const setCampoTrasporto = (campo: keyof TrasportoForm, val: string) => {
    setTrasporto(prev => (prev ? { ...prev, [campo]: val } : prev));
  };

  const salvaTrasporto = async () => {
    if (!ddt || !trasporto) return;

    let colli: number | null = null;
    if (trasporto.colli.trim() !== '') {
      colli = parseInt(trasporto.colli, 10);
      if (!Number.isFinite(colli) || colli < 0) {
        showToast('Numero colli non valido', 'error');
        return;
      }
    }
    let pesoKg: number | null = null;
    if (trasporto.pesoKg.trim() !== '') {
      pesoKg = parseItalianNumber(trasporto.pesoKg);
      if (!Number.isFinite(pesoKg) || pesoKg < 0) {
        showToast('Peso non valido', 'error');
        return;
      }
    }

    setSalvandoTrasporto(true);
    try {
      // @api PUT /api/ddt/:id {"tipoDocumentoId":"number|null","dataTrasporto":"string|null","destinazioneDescrizione":"string|null","destinazioneIndirizzo":"string|null","destinazioneCap":"string|null","destinazioneCitta":"string|null","destinazioneProvincia":"string|null","causaleTrasporto":"string|null","aspettoBeni":"string|null","colli":"number|null","pesoKg":"number|null","porto":"string|null","vettore":"string|null","annotazioni":"string|null"} -> {"success":true}
      await apiFetch<{ success: boolean }>(`/api/ddt/${ddt.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          tipoDocumentoId: trasporto.tipoDocumentoId ? Number(trasporto.tipoDocumentoId) : null,
          dataTrasporto: trasporto.dataTrasporto || null,
          destinazioneDescrizione: trasporto.destinazioneDescrizione.trim() || null,
          destinazioneIndirizzo: trasporto.destinazioneIndirizzo.trim() || null,
          destinazioneCap: trasporto.destinazioneCap.trim() || null,
          destinazioneCitta: trasporto.destinazioneCitta.trim() || null,
          destinazioneProvincia: trasporto.destinazioneProvincia.trim() || null,
          causaleTrasporto: trasporto.causaleTrasporto.trim() || null,
          aspettoBeni: trasporto.aspettoBeni.trim() || null,
          colli,
          pesoKg,
          porto: trasporto.porto.trim() || null,
          vettore: trasporto.vettore.trim() || null,
          annotazioni: trasporto.annotazioni.trim() || null,
        }),
      });
      showToast('Dati di trasporto salvati', 'success');
      setTrasportoSnapshot(JSON.stringify(trasporto));
      await carica();
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nel salvataggio dei dati di trasporto'), 'error');
    } finally {
      setSalvandoTrasporto(false);
    }
  };

  // ── Editing righe ───────────────────────────────────────────────────────────

  const setCampoRiga = (key: string, campo: 'quantita' | 'note', val: string) => {
    setRighe(prev => prev.map(r => (r.key === key ? { ...r, [campo]: val } : r)));
  };

  const rimuoviRiga = (key: string) => {
    setRighe(prev => prev.filter(r => r.key !== key));
  };

  const salvaRighe = async () => {
    if (!ddt) return;
    const payload: Array<{
      rigaOrdineId: number | null;
      componenteId: number | null;
      rigaDistintaId: number | null;
      articoloId: number | null;
      descrizione: string;
      umVendita: string | null;
      quantita: number;
      note: string | null;
    }> = [];
    for (const r of righe) {
      if (!r.descrizione.trim()) {
        showToast('Ogni riga deve avere una descrizione', 'error');
        return;
      }
      const q = Number(r.quantita.replace(',', '.'));
      if (!Number.isFinite(q) || q <= 0) {
        showToast(`Quantità non valida per la riga "${r.descrizione}"`, 'error');
        return;
      }
      payload.push({
        rigaOrdineId: r.rigaOrdineId,
        componenteId: r.componenteId,
        rigaDistintaId: r.rigaDistintaId,
        articoloId: r.articoloId,
        descrizione: r.descrizione.trim(),
        umVendita: r.umVendita,
        quantita: q,
        note: r.note.trim() || null,
      });
    }
    setSalvandoRighe(true);
    try {
      // @api PUT /api/ddt/:id {"righe":[{"rigaOrdineId":"number|null","componenteId":"number|null","rigaDistintaId":"number|null","articoloId":"number|null","descrizione":"string","umVendita":"string|null","quantita":"number","note":"string|null"}]} -> {"success":true}
      await apiFetch<{ success: boolean }>(`/api/ddt/${ddt.id}`, {
        method: 'PUT',
        body: JSON.stringify({ righe: payload }),
      });
      showToast('Righe DDT salvate', 'success');
      await carica();
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nel salvataggio delle righe'), 'error');
    } finally {
      setSalvandoRighe(false);
    }
  };

  const quantitaLettura = (r: RigaLocale): string => {
    const q = Number(r.quantita.replace(',', '.'));
    return Number.isFinite(q) ? formatQuantity(q) : r.quantita;
  };

  // ── Render helpers ──────────────────────────────────────────────────────────

  const badges = useMemo(() => {
    if (!ddt) return null;
    return (
      <div className="ddt-scheda__badges">
        {ddt.tipo === 'VENDITA'
          ? <span className="bop-badge bop-badge-info">Vendita</span>
          : <span className="bop-badge bop-badge-warning">Cantiere</span>}
        {ddt.stato === 'BOZZA' && <span className="bop-badge bop-badge-neutral">Bozza</span>}
        {ddt.stato === 'EMESSO' && <span className="bop-badge bop-badge-info">Emesso</span>}
        {ddt.stato === 'ANNULLATO' && <span className="bop-badge bop-badge-danger">Annullato</span>}
      </div>
    );
  }, [ddt]);

  const indirizzoCliente = (d: DdtDettaglio): string | null => {
    const parti: string[] = [];
    if (d.clienteIndirizzo) parti.push(d.clienteIndirizzo);
    const cittaParti = [d.clienteCap, d.clienteCitta].filter(Boolean).join(' ');
    if (cittaParti) parti.push(d.clienteProvincia ? `${cittaParti} (${d.clienteProvincia})` : cittaParti);
    return parti.length ? parti.join(', ') : null;
  };

  const badgeStatoMovimento = (stato: string): React.ReactNode => {
    const s = stato.toUpperCase();
    if (s.includes('ANNULLAT')) return <span className="bop-badge bop-badge-danger">{stato}</span>;
    if (s.includes('CONFERMAT') || s.includes('REGISTRAT')) return <span className="bop-badge bop-badge-success">{stato}</span>;
    return <span className="bop-badge bop-badge-neutral">{stato}</span>;
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout>
        <div className="ddt-scheda">
          <div className="bop-card">
            <div className="bop-empty-state">Caricamento DDT…</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (errore || !ddt || !trasporto) {
    return (
      <Layout>
        <div className="ddt-scheda">
          <div className="bop-card">
            <div className="bop-empty-state">
              <h3 className="bop-empty-state__title">DDT non disponibile</h3>
              <p className="bop-empty-state__text">{errore || 'DDT non trovato.'}</p>
              <BopBackLink to="/commerciale/attivo/ddt" label="Torna ai DDT" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const d = ddt;
  const mostraGeneraFattura =
    d.tipo === 'VENDITA' && d.stato === 'EMESSO' && !d.fatturaCollegata && canFattureEdit;

  return (
    <Layout>
      <div className="ddt-scheda">
        <BopBackLink to="/commerciale/attivo/ddt" label="Torna ai DDT" />

        {/* Header */}
        <header className="ddt-scheda__header">
          <div className="ddt-scheda__breadcrumb">
            Commerciale ›{' '}
            <Link to="/commerciale/attivo/ddt">DDT</Link> › {d.numero ?? '(bozza)'}
          </div>
          <div className="ddt-scheda__header-row">
            <div className="ddt-scheda__header-left">
              <h1 className="bop-page-title">DDT {d.numero ?? '(bozza)'}</h1>
              {badges}
            </div>
            <div className="ddt-scheda__header-actions">
              <button
                type="button"
                className="bop-btn bop-btn-secondary"
                onClick={apriStampa}
                title="Anteprima, PDF o invio email del DDT con il template di Impostazioni → Report"
              >
                Stampa / Invia
              </button>
              {isBozza && canEdit && (
                <button
                  className="bop-btn bop-btn-primary"
                  onClick={emetti}
                  disabled={azioneInCorso}
                >
                  Emetti DDT
                </button>
              )}
              {isBozza && canDelete && (
                <button
                  className="bop-btn bop-btn-danger"
                  onClick={elimina}
                  disabled={azioneInCorso}
                >
                  Elimina
                </button>
              )}
              {isEmesso && canEdit && (
                <button
                  className="bop-btn bop-btn-danger"
                  onClick={annulla}
                  disabled={azioneInCorso}
                >
                  Annulla DDT
                </button>
              )}
              {d.fatturaCollegata ? (
                <Link
                  className="bop-btn bop-btn-secondary"
                  to={`/commerciale/attivo/fatture/${d.fatturaCollegata.id}`}
                >
                  Fattura {d.fatturaCollegata.numero ?? 'bozza'}
                </Link>
              ) : (
                mostraGeneraFattura && (
                  <button
                    className="bop-btn bop-btn-secondary"
                    onClick={generaFattura}
                    disabled={azioneInCorso}
                  >
                    Genera fattura immediata
                  </button>
                )
              )}
            </div>
          </div>
          {mostraGeneraFattura && (
            <p className="ddt-scheda__nota-fattura bop-text-muted">
              Per la fatturazione riepilogativa mensile usa il wizard Fatturazione differita
            </p>
          )}
        </header>

        {/* Documento */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Documento</h2>
          </div>
          <div className="bop-card__body">
            <div className="ddt-scheda__grid">
              <Campo
                label="Ordine"
                value={
                  d.ordine ? (
                    <Link to={`/commerciale/attivo/ordini/${d.ordine.id}`}>{d.ordine.numero}</Link>
                  ) : null
                }
              />
              <Campo
                label="Commessa"
                value={
                  d.commessa ? (
                    <Link to={`/commerciale/commesse/${d.commessa.id}`}>
                      {d.commessa.numero}
                      {d.commessa.descrizione ? ` — ${d.commessa.descrizione}` : ''}
                    </Link>
                  ) : null
                }
              />
              <Campo label="Cliente" value={d.clienteRagioneSociale} />
              <Campo label="P. IVA / C.F." value={d.clientePartitaIva || d.clienteCodiceFiscale} />
              <Campo label="Indirizzo cliente" value={indirizzoCliente(d)} />
              <Campo label="Data emissione" value={d.dataEmissione ? formatDateIT(d.dataEmissione) : null} />
              {editabile ? (
                <div className="ddt-scheda__campo">
                  <span className="ddt-scheda__campo-label">Tipo documento</span>
                  <select
                    className="bop-select"
                    value={trasporto.tipoDocumentoId}
                    onChange={e => setCampoTrasporto('tipoDocumentoId', e.target.value)}
                  >
                    <option value="">— nessuno (comportamento default) —</option>
                    {tipiDocumentoOptions.map(t => (
                      <option key={t.id} value={String(t.id)}>{t.codice} — {t.nome}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <Campo
                  label="Tipo documento"
                  value={
                    d.tipoDocumento
                      ? `${d.tipoDocumento.nome}${d.tipoDocumento.causaleMovimento ? ` (causale ${d.tipoDocumento.causaleMovimento.codice} — ${d.tipoDocumento.causaleMovimento.descrizione})` : ''}`
                      : null
                  }
                />
              )}
              <Campo
                label="Movimento magazzino"
                value={
                  d.movimentoMagazzino ? (
                    <span className="ddt-scheda__movimento">
                      {d.movimentoMagazzino.numero}/{d.movimentoMagazzino.anno}{' '}
                      {badgeStatoMovimento(d.movimentoMagazzino.stato)}
                    </span>
                  ) : (
                    <span className="bop-text-muted">Nessun movimento generato</span>
                  )
                }
              />
              <Campo
                label="Creato da / il"
                value={
                  nomeUtente(d.createdBy) || d.createdAt
                    ? `${nomeUtente(d.createdBy) || '—'} · ${formatDateTimeIT(d.createdAt)}`
                    : null
                }
              />
              {d.stato === 'ANNULLATO' && (
                <Campo label="Annullato da" value={nomeUtente(d.annullatoDa)} />
              )}
            </div>
          </div>
        </div>

        {/* Destinazione e trasporto */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Destinazione e trasporto</h2>
            {editabile && trasportoDirty && (
              <button
                className="bop-btn bop-btn-primary bop-btn-sm"
                onClick={salvaTrasporto}
                disabled={salvandoTrasporto}
              >
                {salvandoTrasporto ? 'Salvataggio…' : 'Salva'}
              </button>
            )}
          </div>
          <div className="bop-card__body">
            {editabile ? (
              <div className="ddt-scheda__form-grid">
                <div className="bop-form-group">
                  <label className="bop-label">Destinazione (descrizione)</label>
                  <input
                    className="bop-input"
                    value={trasporto.destinazioneDescrizione}
                    onChange={e => setCampoTrasporto('destinazioneDescrizione', e.target.value)}
                    placeholder="Es. cantiere, sede cliente…"
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Indirizzo</label>
                  <input
                    className="bop-input"
                    value={trasporto.destinazioneIndirizzo}
                    onChange={e => setCampoTrasporto('destinazioneIndirizzo', e.target.value)}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">CAP</label>
                  <input
                    className="bop-input"
                    value={trasporto.destinazioneCap}
                    onChange={e => setCampoTrasporto('destinazioneCap', e.target.value)}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Città</label>
                  <input
                    className="bop-input"
                    value={trasporto.destinazioneCitta}
                    onChange={e => setCampoTrasporto('destinazioneCitta', e.target.value)}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Provincia</label>
                  <input
                    className="bop-input"
                    value={trasporto.destinazioneProvincia}
                    onChange={e => setCampoTrasporto('destinazioneProvincia', e.target.value)}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Causale trasporto</label>
                  <input
                    className="bop-input"
                    value={trasporto.causaleTrasporto}
                    onChange={e => setCampoTrasporto('causaleTrasporto', e.target.value)}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Aspetto beni</label>
                  <input
                    className="bop-input"
                    value={trasporto.aspettoBeni}
                    onChange={e => setCampoTrasporto('aspettoBeni', e.target.value)}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Colli</label>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    className="bop-input"
                    value={trasporto.colli}
                    onChange={e => setCampoTrasporto('colli', e.target.value)}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Peso (kg)</label>
                  <input
                    className="bop-input"
                    value={trasporto.pesoKg}
                    onChange={e => setCampoTrasporto('pesoKg', e.target.value)}
                    placeholder="0,00"
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Porto</label>
                  <input
                    className="bop-input"
                    value={trasporto.porto}
                    onChange={e => setCampoTrasporto('porto', e.target.value)}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Vettore</label>
                  <input
                    className="bop-input"
                    value={trasporto.vettore}
                    onChange={e => setCampoTrasporto('vettore', e.target.value)}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Data trasporto</label>
                  <input
                    type="date"
                    className="bop-input"
                    value={trasporto.dataTrasporto}
                    onChange={e => setCampoTrasporto('dataTrasporto', e.target.value)}
                  />
                </div>
                <div className="bop-form-group ddt-scheda__form-full">
                  <label className="bop-label">Annotazioni</label>
                  <textarea
                    className="bop-input ddt-scheda__annotazioni"
                    value={trasporto.annotazioni}
                    onChange={e => setCampoTrasporto('annotazioni', e.target.value)}
                    rows={3}
                  />
                </div>
              </div>
            ) : (
              <div className="ddt-scheda__grid">
                <Campo label="Destinazione (descrizione)" value={d.destinazioneDescrizione} />
                <Campo label="Indirizzo" value={d.destinazioneIndirizzo} />
                <Campo label="CAP" value={d.destinazioneCap} />
                <Campo label="Città" value={d.destinazioneCitta} />
                <Campo label="Provincia" value={d.destinazioneProvincia} />
                <Campo label="Causale trasporto" value={d.causaleTrasporto} />
                <Campo label="Aspetto beni" value={d.aspettoBeni} />
                <Campo label="Colli" value={d.colli !== null ? String(d.colli) : null} />
                <Campo
                  label="Peso (kg)"
                  value={
                    d.pesoKg !== null && d.pesoKg !== undefined
                      ? formatQuantity(Number(d.pesoKg))
                      : null
                  }
                />
                <Campo label="Porto" value={d.porto} />
                <Campo label="Vettore" value={d.vettore} />
                <Campo label="Data trasporto" value={d.dataTrasporto ? formatDateIT(d.dataTrasporto) : null} />
                <Campo label="Annotazioni" value={d.annotazioni} />
              </div>
            )}
          </div>
        </div>

        {/* Righe */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Righe</h2>
            {editabile && (
              <button
                className="bop-btn bop-btn-primary bop-btn-sm"
                onClick={salvaRighe}
                disabled={salvandoRighe || !righeDirty}
              >
                {salvandoRighe ? 'Salvataggio…' : 'Salva righe'}
              </button>
            )}
          </div>
          <div className="bop-card__body">
            {righe.length === 0 ? (
              <div className="bop-empty-state">
                <p className="bop-empty-state__text">Nessuna riga nel DDT.</p>
              </div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Pos</th>
                    <th>Codice</th>
                    <th>Descrizione</th>
                    <th>U.M.</th>
                    <th className="bop-text-right">Quantità</th>
                    <th>Note</th>
                    {editabile && <th className="bop-table__th--actions"></th>}
                  </tr>
                </thead>
                <tbody>
                  {righe.map((r, i) => (
                    <tr key={r.key}>
                      <td>{i + 1}</td>
                      <td>{r.codice || <span className="bop-text-muted">—</span>}</td>
                      <td>{r.descrizione}</td>
                      <td>{r.umVendita || <span className="bop-text-muted">—</span>}</td>
                      <td className="bop-text-right">
                        {editabile ? (
                          <input
                            className="bop-input ddt-scheda__qta-input"
                            value={r.quantita}
                            onChange={e => setCampoRiga(r.key, 'quantita', e.target.value)}
                          />
                        ) : (
                          quantitaLettura(r)
                        )}
                      </td>
                      <td>
                        {editabile ? (
                          <input
                            className="bop-input ddt-scheda__note-input"
                            value={r.note}
                            onChange={e => setCampoRiga(r.key, 'note', e.target.value)}
                            placeholder="Note"
                          />
                        ) : (
                          r.note || <span className="bop-text-muted">—</span>
                        )}
                      </td>
                      {editabile && (
                        <td className="bop-table__td--actions">
                          <button
                            className="bop-btn bop-btn-danger bop-btn-sm"
                            onClick={() => rimuoviRiga(r.key)}
                          >
                            Rimuovi
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Log attività */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Log attività</h2>
          </div>
          <div className="bop-card__body">
            {(d.logs || []).length === 0 ? (
              <div className="bop-empty-state">Nessuna voce di log.</div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Data/ora</th>
                    <th>Utente</th>
                    <th>Azione</th>
                    <th>Dettaglio</th>
                  </tr>
                </thead>
                <tbody>
                  {d.logs.map(l => (
                    <tr key={l.id}>
                      <td>{formatDateTimeIT(l.createdAt)}</td>
                      <td>{l.utenteName}</td>
                      <td>{l.azione}</td>
                      <td>{l.dettaglio || <span className="bop-text-muted">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {modalStampa && (
          <PrintModal
            societaId={d.societaId}
            entitaType="ddt"
            entitaId={d.id}
            entitaLabel={`DDT ${d.numero ?? 'bozza'}`}
            onClose={() => setModalStampa(false)}
          />
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
